/**
 * Workbench — second controlled draft lane deep panel (context / proposal / receipt / recovery).
 * Projection-only · grantsAuthority=false.
 */
import React, { useMemo } from 'react';
import { Alert, Space, Tag, Typography } from 'antd';
import { feSafeWorkbenchControlledLaneViewV1 } from '@shared/ai-controlled-execution-second-operational-lane/surfaces/feSafeDemoProjectionV1';

const { Text, Paragraph } = Typography;

export interface ControlledSecondDraftLanePanelProps {
  tenantId: string;
}

export function ControlledSecondDraftLanePanel({
  tenantId: _tenantId,
}: ControlledSecondDraftLanePanelProps): React.ReactElement {
  const vm = useMemo(() => feSafeWorkbenchControlledLaneViewV1(), []);

  return (
    <section
      data-testid="ai-second-lane-controlled-draft-panel"
      aria-label="AI second controlled draft lane"
      style={{ marginTop: 12 }}
    >
      <Space wrap style={{ marginBottom: 8 }}>
        <Text strong>Second controlled draft lane</Text>
        <Tag data-testid="ai-second-lane-ui-state">{vm.uiState}</Tag>
        <Tag data-testid="ai-second-lane-grants-authority">grantsAuthority=false</Tag>
        <Tag data-testid="ai-second-lane-provenance">{vm.provenance}</Tag>
      </Space>
      <Paragraph data-testid="ai-second-lane-proposal">{vm.proposalDetails}</Paragraph>
      <dl>
        <div>
          <dt>FlowPurpose</dt>
          <dd data-testid="ai-second-lane-flow">{vm.flowPurpose}</dd>
        </div>
        <div>
          <dt>Expected effect</dt>
          <dd data-testid="ai-second-lane-effect">{vm.expectedEffect}</dd>
        </div>
        <div>
          <dt>Authority</dt>
          <dd data-testid="ai-second-lane-authority">{vm.authorityDecision}</dd>
        </div>
        <div>
          <dt>Approval</dt>
          <dd data-testid="ai-second-lane-approval">{vm.approvalStatus}</dd>
        </div>
        <div>
          <dt>Execution</dt>
          <dd data-testid="ai-second-lane-execution">{vm.executionStatus}</dd>
        </div>
        <div>
          <dt>Receipt</dt>
          <dd data-testid="ai-second-lane-receipt">{vm.receiptId ?? 'none'}</dd>
        </div>
        <div>
          <dt>Assumptions</dt>
          <dd data-testid="ai-second-lane-assumptions">
            {vm.factsVsAssumptions.assumptions.join(' · ')}
          </dd>
        </div>
      </dl>
      {vm.recovery ? (
        <Alert
          type="warning"
          showIcon
          message={vm.recovery}
          data-testid="ai-second-lane-recovery"
        />
      ) : null}
    </section>
  );
}
